import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CheckCircle2, ChevronLeft, PlugZap, Save, Trash2, XCircle } from 'lucide-react'
import { notiApi, type ChannelTestResult, type NotiChannel } from '../lib/api-noti'
import { Toggle } from '../components/Toggle'
import { CHANNEL_META, ago } from './ChannelsPage'

// Per-type config fields. Secret-bearing values come back redacted ('•••') —
// leaving a redacted value untouched keeps the stored secret (config is sent
// whole, so we strip untouched-redacted keys client-side before PATCH).
const CONFIG_FIELDS: Record<string, { key: string; label: string; placeholder?: string; secret?: boolean }[]> = {
  email: [
    { key: 'host', label: 'SMTP host', placeholder: 'baacloan-mailpit (ว่าง = ใช้ค่า env ของ engine)' },
    { key: 'port', label: 'SMTP port', placeholder: '1025' },
    { key: 'user', label: 'Username', placeholder: '(ว่างได้)' },
    { key: 'pass', label: 'Password', secret: true },
    { key: 'from', label: 'From', placeholder: 'BAAC Notification <no-reply@baac.local>' },
  ],
  sms: [
    { key: 'mode', label: 'Mode (mock | http)', placeholder: 'mock' },
    { key: 'url', label: 'Gateway URL (http mode)', placeholder: 'https://sms-gw…/send' },
    { key: 'apiKey', label: 'API key', secret: true },
    { key: 'from', label: 'Sender name', placeholder: 'BAAC' },
  ],
  fcm: [
    { key: 'serviceAccount', label: 'Service Account JSON (ทั้งก้อน)', secret: true },
  ],
  webhook: [
    { key: 'url', label: 'Endpoint URL', placeholder: 'https://ระบบปลายทาง/notify' },
  ],
  mock: [],
}

export default function ChannelDetailPage() {
  const { channelId } = useParams({ strict: false }) as { channelId: string }
  const qc = useQueryClient()
  const navigate = useNavigate()
  const channel = useQuery({ queryKey: ['noti-channel', channelId], queryFn: () => notiApi.getChannel(channelId) })
  const [testResult, setTestResult] = useState<ChannelTestResult | null>(null)
  const [testTo, setTestTo] = useState('')

  const test = useMutation({
    mutationFn: (to?: string) => notiApi.testChannel(channelId, to),
    onSuccess: (r) => {
      setTestResult(r)
      qc.invalidateQueries({ queryKey: ['noti-channel', channelId] })
      qc.invalidateQueries({ queryKey: ['noti-channels'] })
    },
  })
  const toggle = useMutation({
    mutationFn: (enabled: boolean) => notiApi.patchChannel(channelId, { enabled }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['noti-channel', channelId] }),
  })
  const remove = useMutation({
    mutationFn: () => notiApi.deleteChannel(channelId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['noti-channels'] })
      navigate({ to: '/channels' })
    },
  })

  const ch = channel.data
  if (channel.isLoading) return <div className="panel">กำลังโหลด…</div>
  if (channel.isError || !ch) return <div className="panel error">{(channel.error as Error)?.message ?? 'ไม่พบ channel'}</div>

  const meta = CHANNEL_META[ch.type]
  const result = testResult ?? (ch.lastTestStatus
    ? { id: ch.id, status: ch.lastTestStatus as 'ok' | 'failed', detail: ch.lastTestDetail ?? '', testedAt: ch.lastTestAt ?? '' }
    : null)
  const cfgStr = (k: string) => (typeof ch.config[k] === 'string' ? (ch.config[k] as string) : '')

  return (
    <>
      <div className="page-head">
        <h2>
          <Link to="/channels" className="back-link"><ChevronLeft size={18} /></Link>
          <meta.icon size={20} /> {ch.name}
          <code className="head-code">{ch.id}</code>
          <span className="pill">{meta.label}</span>
        </h2>
        <div className="toggle-row">
          <Toggle checked={ch.enabled} disabled={toggle.isPending} onChange={(v) => toggle.mutate(v)} />
          <span className="muted small">{ch.enabled ? 'เปิดใช้งาน' : 'ปิด — delivery ที่ชี้มาจะถูก skipped'}</span>
        </div>
      </div>

      <div className="two-col">
        <ConfigPanel ch={ch} />

        <div className="panel">
          <h3><PlugZap size={16} /> การเชื่อมต่อ</h3>
          {ch.type === 'email' && (
            <div className="muted small" style={{ margin: '0 0 0.8rem', lineHeight: 1.9 }}>
              <div style={{ fontWeight: 600, marginBottom: '0.2rem' }}>SMTP ปัจจุบันของ channel นี้</div>
              <div>Host / Port: <code>{cfgStr('host') || 'env (Mailpit)'}</code> : <code>{cfgStr('port') || 'env'}</code></div>
              <div>Username: <code>{cfgStr('user') || '—'}</code></div>
              <div>From: <code>{cfgStr('from') || 'env'}</code></div>
            </div>
          )}
          <p className="muted small" style={{ margin: '0 0 0.7rem' }}>
            {ch.type === 'email' && 'ทดสอบ: ใส่อีเมลผู้รับเพื่อส่งเมลทดสอบจริง — เว้นว่างจะเป็นแค่ EHLO probe (เชื่อมต่อ + AUTH)'}
            {ch.type === 'fcm' && 'ทดสอบ: ใช้ service account ขอ OAuth token จาก Google จริง'}
            {ch.type === 'webhook' && 'ทดสอบ: HTTP ping ไปยัง endpoint ปลายทาง'}
            {ch.type === 'sms' && 'ทดสอบ: โหมด mock ตอบสำเร็จเสมอ / โหมด http จะ ping gateway'}
            {ch.type === 'mock' && 'channel ทดสอบ — พร้อมใช้งานเสมอ'}
          </p>
          {ch.type === 'email' && (
            <div className="form-grid" style={{ marginBottom: '0.7rem' }}>
              <label>อีเมลผู้รับ (ส่งเมลทดสอบจริง)
                <input type="email" value={testTo} spellCheck={false}
                  placeholder='you@example.com — ว่าง = แค่ EHLO probe'
                  onChange={(e) => setTestTo(e.target.value)} />
              </label>
            </div>
          )}
          <button className="btn-primary" disabled={test.isPending}
            onClick={() => test.mutate(ch.type === 'email' ? testTo.trim() || undefined : undefined)}>
            <PlugZap size={15} /> {test.isPending
              ? 'กำลังทดสอบ…'
              : ch.type === 'email' && testTo.trim() ? 'ส่งเมลทดสอบ' : 'Test connection'}
          </button>
          {test.isError && <div className="error" style={{ marginTop: '0.6rem' }}>{(test.error as Error).message}</div>}
          {result && (
            <div className={`test-result ${result.status}`}>
              {result.status === 'ok' ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
              <div>
                <b>{result.status === 'ok' ? 'เชื่อมต่อสำเร็จ' : 'เชื่อมต่อไม่สำเร็จ'}</b>
                <div className="small">{result.detail}</div>
                <div className="muted small">{ago(result.testedAt)}</div>
              </div>
            </div>
          )}

          {ch.type === 'email' && (
            <details style={{ marginTop: '1rem' }}>
              <summary style={{ cursor: 'pointer', fontWeight: 600 }}>วิธีตั้งค่า Gmail / Google Workspace SMTP</summary>
              <div className="muted small" style={{ lineHeight: 1.9, marginTop: '0.5rem' }}>
                <b>1. กรอกที่ Configuration (ฝั่งซ้าย)</b>
                <div>• SMTP host = <code>smtp.gmail.com</code> · port = <code>587</code></div>
                <div>• Username = อีเมล Gmail/Workspace (เช่น <code>you@company.com</code>)</div>
                <div>• Password = <b>App Password 16 ตัว</b> (ไม่ใช่รหัสผ่านปกติ — Google บล็อก basic auth แล้ว)</div>
                <div>• From = <code>ชื่อผู้ส่ง &lt;อีเมลเดียวกับ Username&gt;</code></div>
                <b style={{ display: 'block', marginTop: '0.6rem' }}>2. ขอ App Password</b>
                <div>① เปิด <b>2-Step Verification</b> ก่อน (<code>myaccount.google.com/security</code>)</div>
                <div>② ไป <code>myaccount.google.com/apppasswords</code> → ตั้งชื่อ → Create</div>
                <div>③ copy รหัส <b>16 ตัว</b> ตัดช่องว่างออก → วางช่อง Password</div>
                <div>※ Workspace: admin ต้องอนุญาต app passwords; ถ้าขึ้น “not available” ให้ใช้บัญชี Gmail ส่วนตัวแทน</div>
                <b style={{ display: 'block', marginTop: '0.6rem' }}>3. เปลี่ยนเป็นอีเมลอื่น (สมาชิกทีม)</b>
                <div>แก้ Username + From + Password (app password ของอีเมลใหม่) → <b>บันทึก configuration</b> → กด Test connection (ใส่อีเมลผู้รับเพื่อส่งทดสอบจริง)</div>
              </div>
            </details>
          )}

          <div className="danger-zone" style={{ marginTop: '1.2rem' }}>
            <h3>ลบ Channel</h3>
            {remove.isError && <div className="error">{(remove.error as Error).message}</div>}
            <button className="btn-danger" disabled={remove.isPending} onClick={() => {
              if (confirm(`ยืนยันลบ channel ${ch.id}?`)) remove.mutate()
            }}>
              <Trash2 size={15} /> ลบ Channel นี้
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

function ConfigPanel({ ch }: { ch: NotiChannel }) {
  const qc = useQueryClient()
  const fields = CONFIG_FIELDS[ch.type] ?? []
  const [values, setValues] = useState<Record<string, string>>({})

  useEffect(() => {
    const v: Record<string, string> = {}
    for (const f of fields) {
      const raw = ch.config[f.key]
      v[f.key] = typeof raw === 'string' ? raw : raw == null ? '' : JSON.stringify(raw, null, 2)
    }
    setValues(v)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ch.id, ch.updatedAt])

  const save = useMutation({
    mutationFn: () => {
      const config: Record<string, unknown> = {}
      for (const f of fields) {
        const v = values[f.key] ?? ''
        if (v === '' || v === '•••') continue // untouched secret / empty → leave out
        if (f.key === 'serviceAccount') {
          config[f.key] = JSON.parse(v)
        } else {
          config[f.key] = v
        }
      }
      // Preserve stored secrets the admin did not retype.
      for (const [k, old] of Object.entries(ch.config)) {
        if (!(k in config) && values[k] === '•••') config[k] = old
      }
      return notiApi.patchChannel(ch.id, { config })
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['noti-channel', ch.id] }),
  })

  return (
    <div className="panel">
      <h3>Configuration</h3>
      {fields.length === 0 ? (
        <p className="muted" style={{ margin: 0 }}>channel ชนิดนี้ไม่มีค่าให้ตั้ง</p>
      ) : (
        <div className="form-grid">
          {fields.map((f) => (
            <label key={f.key}>{f.label}
              {f.key === 'serviceAccount' ? (
                <textarea rows={7} value={values[f.key] ?? ''} spellCheck={false}
                  placeholder='{"type":"service_account","project_id":"…", …}'
                  onChange={(e) => setValues((s) => ({ ...s, [f.key]: e.target.value }))} />
              ) : (
                <input type={f.secret ? 'password' : 'text'} value={values[f.key] ?? ''} spellCheck={false}
                  placeholder={f.placeholder} onChange={(e) => setValues((s) => ({ ...s, [f.key]: e.target.value }))} />
              )}
            </label>
          ))}
        </div>
      )}
      <p className="muted small">
        ค่าว่าง = engine ใช้ค่า environment ของตัวเอง (เช่น Mailpit บน x10) · ค่าลับแสดงเป็น ••• — พิมพ์ทับเพื่อเปลี่ยน
      </p>
      {save.isError && <div className="error">{(save.error as Error).message}</div>}
      {fields.length > 0 && (
        <button className="btn-primary" disabled={save.isPending} onClick={() => save.mutate()}>
          <Save size={15} /> บันทึก configuration
        </button>
      )}
    </div>
  )
}
